/**
 * Pagina dettaglio cliente.
 *
 * Un cliente e' un Soggetto con tipo='cliente', letto dall'endpoint generico
 * /api/v1/soggetti/{id}. Mostra i dati anagrafici in sola lettura con bottone
 * "Modifica" verso il form di modifica.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface ClienteData {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
  codice_fiscale: string | null;
  partita_iva: string | null;
  email: string | null;
  telefono: string | null;
  pec: string | null;
  codice_sdi: string | null;
  indirizzo: string | null;
  civico: string | null;
  cap: string | null;
  comune: string | null;
  provincia: string | null;
  nazione: string | null;
  stato: string;
  created_at: string;
  updated_at: string;
}

/**
 * Pagina dettaglio cliente con dati anagrafici in sola lettura.
 */
const ClienteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState<ClienteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const caricaCliente = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/soggetti/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: ClienteData = await response.json();
      setCliente(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void caricaCliente();
  }, [caricaCliente]);

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento...
      </p>
    );
  }

  if (error || !cliente) {
    return (
      <p className="p-6 text-center text-sm text-destructive">
        {error ?? 'Cliente non trovato'}
      </p>
    );
  }

  /** Nome visualizzabile del cliente. */
  const displayName =
    cliente.ragione_sociale ??
    [cliente.cognome, cliente.nome].filter(Boolean).join(' ') ??
    '-';

  return (
    <div className="space-y-6 p-6">
      {/* Intestazione */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          {displayName || '-'}
        </h1>
        <Badge variant={cliente.stato === 'attivo' ? 'default' : 'secondary'}>
          {cliente.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
        </Badge>
        <div className="ml-auto">
          <Button
            variant="outline"
            onClick={() => navigate(`/clienti/${cliente.id}/modifica`)}
          >
            Modifica
          </Button>
        </div>
      </div>

      {/* Dati anagrafici */}
      <Card>
        <CardHeader>
          <CardTitle>Dati anagrafici</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
            {cliente.ragione_sociale && (
              <div>
                <p className="text-muted-foreground">Ragione sociale</p>
                <p className="font-medium">{cliente.ragione_sociale}</p>
              </div>
            )}
            {cliente.nome && (
              <div>
                <p className="text-muted-foreground">Nome</p>
                <p className="font-medium">{cliente.nome}</p>
              </div>
            )}
            {cliente.cognome && (
              <div>
                <p className="text-muted-foreground">Cognome</p>
                <p className="font-medium">{cliente.cognome}</p>
              </div>
            )}
            <div>
              <p className="text-muted-foreground">P.IVA</p>
              <p className="font-mono">{cliente.partita_iva ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Codice fiscale</p>
              <p className="font-mono">{cliente.codice_fiscale ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Codice SDI</p>
              <p className="font-mono">{cliente.codice_sdi ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Email</p>
              <p>{cliente.email ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">PEC</p>
              <p>{cliente.pec ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Telefono</p>
              <p>{cliente.telefono ?? '-'}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-muted-foreground">Indirizzo</p>
              <p className="font-medium">
                {[cliente.indirizzo, cliente.civico]
                  .filter(Boolean)
                  .join(', ') || '-'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">CAP</p>
              <p>{cliente.cap ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Comune</p>
              <p>{cliente.comune ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Provincia</p>
              <p>{cliente.provincia ?? '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Nazione</p>
              <p>{cliente.nazione ?? '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClienteDetail;
